import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import "./Dashboard.css";
import Cards from "../components/cards/Cards";
import MyDialog from "../components/dialog/Dialog";
import BillReminder from "./BillReminder";

import {
  activeCreditsReport,
  activeOrdersReport,
  mostPurchasesReport,
  nearToFInishReport,
  profitTodayTotalReport,
  salesTodayTotalReport,
  salesTodayTotalReportWithdraw,
  stockValueReport,
  subscriptionBills,
  floatCashBalance,
} from "../app/reportsSlice";

import { hasAccess, instCat } from "../common/validators";

const BUSINESS_TYPE = {
  FLOAT_PROVIDER: 4,
  SERVICE_PROVIDER: 5,
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isBillDialogOpen, setIsBillDialogOpen] = useState(false);
  const [totalStock, setTotalStock] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [cashBalances, setCashBalances] = useState({
    openCash: 0,
    currentCash: 0,
    openFloat: 0,
    currentFloat: 0,
  });
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const businessCategory = useMemo(() => instCat(), []);
  const canViewStaffData = useMemo(() => hasAccess("staff", "view"), []);

  const {
    error: billsError,
    status: billsStatus,
    list: billsList,
  } = useSelector(({ reports }) => reports.bills.get);

  const {
    error: cashFloatsError,
    status: cashFloatsStatus,
    list: cashFloatsList,
  } = useSelector(({ reports }) => reports.cashFloats.get);

  const {
    error: ordersError,
    status: ordersStatus,
    list: ordersList,
  } = useSelector(({ reports }) => reports.dashboard.activeOrders);

  const {
    error: creditsError,
    status: creditsStatus,
    list: creditsList,
  } = useSelector(({ reports }) => reports.dashboard.activeCredits);

  const {
    error: stockValueError,
    status: stockValueStatus,
    list: stockValueList,
  } = useSelector(({ reports }) => reports.dashboard.stockValue);

  const {
    error: mostPurchasedError,
    status: mostPurchasedStatus,
    list: mostPurchasedList,
  } = useSelector(({ reports }) => reports.dashboard.mostPurchased);

  const {
    error: leastInStockError,
    status: leastInStockStatus,
    list: leastInStockList,
  } = useSelector(({ reports }) => reports.dashboard.leastInStock);

  const {
    error: salesTodayError,
    status: salesTodayStatus,
    list: salesTodayList,
  } = useSelector(({ reports }) => reports.dashboard.salesToday);

  const {
    error: salesTodayErrorWithdraw,
    status: salesTodayStatusWithdraw,
    list: salesTodayListWithdraw,
  } = useSelector(({ reports }) => reports.dashboard.salesTodayWithdraw);

  const {
    error: profitTodayError,
    status: profitTodayStatus,
    list: profitTodayList,
  } = useSelector(({ reports }) => reports.dashboard.profitToday);

  // ---------- helpers ----------

  const safeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatNumber = (value) => safeNumber(value).toLocaleString();

  const getFirstValue = (list, field, fallback = 0) => {
    if (!Array.isArray(list) || list.length === 0) return fallback;
    const value = list[0]?.[field];
    return value ?? fallback;
  };

  const navigateTo = (path) => {
    if (path) navigate(path);
  };

  // ---------- data loading ----------

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/intro");
      return;
    }

    const businessDate = moment(new Date()).format("YYYY-MM-DD");

    dispatch(activeCreditsReport());
    dispatch(activeOrdersReport());
    dispatch(stockValueReport());
    dispatch(mostPurchasesReport());
    dispatch(nearToFInishReport());
    dispatch(salesTodayTotalReport());
    dispatch(subscriptionBills());
    dispatch(salesTodayTotalReportWithdraw());
    dispatch(
      floatCashBalance({
        businessDate,
      })
    );
    dispatch(
      profitTodayTotalReport({
        businessDate,
      })
    );
  }, [dispatch, navigate]);

  // ---------- derived state ----------

  useEffect(() => {
    if (!Array.isArray(stockValueList) || stockValueList.length === 0) {
      return;
    }

    if (businessCategory === BUSINESS_TYPE.FLOAT_PROVIDER) {
      setTotalStock(cashBalances.currentFloat);
      return;
    }

    setTotalStock(safeNumber(stockValueList[0]?.w_stock_value));
  }, [stockValueList, businessCategory, cashBalances.currentFloat]);

  useEffect(() => {
    if (!Array.isArray(cashFloatsList) || cashFloatsList.length === 0) {
      return;
    }

    const totals = cashFloatsList.reduce(
      (acc, item) => ({
        openFloat: acc.openFloat + safeNumber(item?.device_balance),
        openCash: acc.openCash + safeNumber(item?.droo_balance),
        currentCash: acc.currentCash + safeNumber(item?.cash_balance),
        currentFloat: acc.currentFloat + safeNumber(item?.float_balance),
      }),
      {
        openFloat: 0,
        openCash: 0,
        currentCash: 0,
        currentFloat: 0,
      }
    );

    setCashBalances(totals);
  }, [cashFloatsList, cashFloatsStatus, cashFloatsError]);

  useEffect(() => {
    if (!Array.isArray(mostPurchasedList) || mostPurchasedList.length === 0) {
      return;
    }

    setChartSeries(
      mostPurchasedList.map((item) => safeNumber(item?.amount))
    );

    setChartLabels(
      mostPurchasedList.map((item) => item?.item_description ?? "Unknown")
    );
  }, [mostPurchasedList]);

  useEffect(() => {
    if (
      profitTodayError ||
      !Array.isArray(profitTodayList) ||
      profitTodayList.length === 0
    ) {
      return;
    }

    const first = profitTodayList[0];
    const sellAmount = safeNumber(first?.sell_amount);
    const stockPrice = first?.stock_price;

    if (stockPrice === null || stockPrice === undefined) {
      setTotalProfit(0);
      return;
    }

    setTotalProfit(sellAmount - safeNumber(stockPrice));
  }, [profitTodayList, profitTodayStatus, profitTodayError]);

  useEffect(() => {
    setIsBillDialogOpen(Array.isArray(billsList) && billsList.length > 0);
  }, [billsList]);

  // ---------- chart ----------

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        toolbar: { show: false },
      },
      labels: chartLabels,
      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
      },
      tooltip: {
        y: {
          formatter: (value) => formatNumber(value),
        },
      },
      colors: [
        "#2563eb",
        "#16a34a",
        "#f59e0b",
        "#ef4444",
        "#8b5cf6",
        "#06b6d4",
      ],
    }),
    [chartLabels]
  );

  const chartData = useMemo(
    () => ({
      series: chartSeries,
      options: chartOptions,
    }),
    [chartSeries, chartOptions]
  );

  // ---------- UI subcomponents ----------

  const SectionCard = ({ title, children, action }) => (
    <div className="dashboard-card">
      <div className="dashboard-card-header">
        <h3 className="dashboard-card-title">{title}</h3>
        {action}
      </div>
      <div className="dashboard-card-body">{children}</div>
    </div>
  );

  const KpiCard = ({ title, value, accent = "#2563eb", onClick }) => (
    <div
      className="kpi-card"
      style={{ borderLeftColor: accent }}
      onClick={() => onClick && onClick()}
    >
      <div className="kpi-card-label">{title}</div>
      <div className="kpi-card-value">{value}</div>
    </div>
  );

  const DataTable = ({ columns, rows, emptyMessage }) => (
    <div className="table-wrapper">
      <table className="dashboard-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.align === "right" ? "text-right" : ""}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row?.id ?? index}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={column.align === "right" ? "text-right" : ""}
                  >
                    {column.render
                      ? column.render(row)
                      : row?.[column.key] ?? "-"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // ---------- sections ----------

  const renderCashSummary = () => (
    <div className="cash-summary-grid">
      <div className="cash-summary-item">
        <span className="cash-summary-label">Open Cash</span>
        <span className="cash-summary-value">
          {formatNumber(cashBalances.openCash)}
        </span>
      </div>

      <div className="cash-summary-item">
        <span className="cash-summary-label">Current Cash</span>
        <span className="cash-summary-value">
          {formatNumber(cashBalances.currentCash)}
        </span>
      </div>

      <div className="cash-summary-item">
        <span className="cash-summary-label">Open Float</span>
        <span className="cash-summary-value">
          {formatNumber(cashBalances.openFloat)}
        </span>
      </div>

      <div className="cash-summary-item">
        <span className="cash-summary-label">Current Float</span>
        <span className="cash-summary-value">
          {formatNumber(cashBalances.currentFloat)}
        </span>
      </div>
    </div>
  );

  const renderFloatProviderDashboard = () => (
    <>
      <SectionCard title="Cash & Float Overview">
        {renderCashSummary()}
      </SectionCard>

      <div className="kpi-grid">
        <KpiCard
          title="Deposits Today"
          value={formatNumber(getFirstValue(salesTodayList, "amount"))}
          accent="#16a34a"
        />
        <KpiCard
          title="Withdrawals Today"
          value={formatNumber(
            getFirstValue(salesTodayListWithdraw, "amount")
          )}
          accent="#ef4444"
        />
        <KpiCard
          title="Commissions Today"
          value={formatNumber(totalProfit)}
          accent="#2563eb"
        />
        <KpiCard
          title="Float Value"
          value={formatNumber(totalStock)}
          accent="#f59e0b"
        />
      </div>

      <div className="dashboard-grid">
        <SectionCard title="Most Transactions">
          <DataTable
            columns={[
              { key: "item_description", label: "Item" },
              { key: "qty", label: "Qty", align: "right", render: (row) => formatNumber(row?.qty) },
              ...(canViewStaffData
                ? [
                    {
                      key: "amount",
                      label: "Amount",
                      align: "right",
                      render: (row) => formatNumber(row?.amount),
                    },
                  ]
                : []),
            ]}
            rows={mostPurchasedList ?? []}
            emptyMessage="No transactions available"
          />
        </SectionCard>

        <SectionCard title="Transaction Distribution">
          <ReactApexChart
            options={chartData.options}
            series={chartData.series}
            type="donut"
            height={320}
          />
        </SectionCard>
      </div>
    </>
  );

  const renderStandardDashboard = () => (
    <>
      <div className="kpi-grid">
        <KpiCard
          title="Sales Today"
          value={formatNumber(getFirstValue(salesTodayList, "amount"))}
          accent="#2563eb"
          onClick={() => navigateTo("/sales")}
        />

        {canViewStaffData && (
          <KpiCard
            title="Profit Today"
            value={formatNumber(totalProfit)}
            accent="#16a34a"
          />
        )}

        <KpiCard
          title="Active Orders"
          value={formatNumber(getFirstValue(ordersList, "amount"))}
          accent="#f59e0b"
          onClick={() => navigateTo("/orders")}
        />

        <KpiCard
          title="Credits Unpaid"
          value={formatNumber(getFirstValue(creditsList, "amount"))}
          accent="#ef4444"
          onClick={() => navigateTo("/credits")}
        />

        {businessCategory !== BUSINESS_TYPE.SERVICE_PROVIDER &&
          canViewStaffData && (
            <KpiCard
              title="Stock Value"
              value={formatNumber(totalStock)}
              accent="#8b5cf6"
            />
          )}

        {!canViewStaffData && (
          <KpiCard
            title="Sales Today"
            value={formatNumber(getFirstValue(salesTodayList, "amount"))}
            accent="#2563eb"
            onClick={() => navigateTo("/sales")}
          />
        )}
      </div>

      <div className="dashboard-grid">
        <SectionCard
          title={
            businessCategory === BUSINESS_TYPE.SERVICE_PROVIDER
              ? "Most Purchased Services"
              : "Most Purchased Items"
          }
        >
          <DataTable
            columns={[
              {
                key: "item_description",
                label:
                  businessCategory === BUSINESS_TYPE.SERVICE_PROVIDER
                    ? "Service"
                    : "Item",
              },
              {
                key: "qty",
                label: "Qty",
                align: "right",
                render: (row) => formatNumber(row?.qty),
              },
              ...(canViewStaffData
                ? [
                    {
                      key: "amount",
                      label: "Amount",
                      align: "right",
                      render: (row) => formatNumber(row?.amount),
                    },
                  ]
                : []),
            ]}
            rows={mostPurchasedList ?? []}
            emptyMessage="No purchase data available"
          />
        </SectionCard>

        {businessCategory !== BUSINESS_TYPE.SERVICE_PROVIDER && (
          <SectionCard title="Least In Stock">
            <DataTable
              columns={[
                { key: "item_description", label: "Item" },
                {
                  key: "balance_qty",
                  label: "Qty",
                  align: "right",
                  render: (row) => formatNumber(row?.balance_qty),
                },
                ...(canViewStaffData
                  ? [
                      {
                        key: "stock_value",
                        label: "Stock Value",
                        align: "right",
                        render: (row) => formatNumber(row?.stock_value),
                      },
                    ]
                  : []),
              ]}
              rows={leastInStockList ?? []}
              emptyMessage="No stock data available"
            />
          </SectionCard>
        )}
      </div>
    </>
  );

  const renderBillReminder = () => (
    <BillReminder
      billData={isBillDialogOpen ? billsList?.[0] ?? null : null}
    />
  );

  // ---------- main render ----------

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {businessCategory === BUSINESS_TYPE.FLOAT_PROVIDER
          ? renderFloatProviderDashboard()
          : renderStandardDashboard()}
      </div>

      <MyDialog
        title="Subscription - Pending Bill"
        width="md"
        open={isBillDialogOpen}
        onClose={() => setIsBillDialogOpen(false)}
        text="Subscription - Pending Bill"
        content={renderBillReminder}
        hideSaveButton
        propBackgroundColor="#367588"
      />
    </div>
  );
};

export default Dashboard;